/**
 * @fileoverview General purpose utility functions for the frontend.
 *
 * This module contains shared helpers for class name merging (Tailwind + clsx)
 * and security-critical utilities like redirect path validation.
 *
 * @module lib/utils
 */

import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merges Tailwind CSS classes with clsx for conditional class names.
 *
 * @description
 * Combines `clsx` for conditional logic and `tailwind-merge` to handle
 * Tailwind class conflicts (e.g., overriding `p-4` with `p-8`).
 *
 * @param inputs - List of class names, arrays, or objects
 * @returns Merged class string
 *
 * @example
 * ```tsx
 * import { cn } from "@/lib/utils";
 *
 * // Basic usage
 * <div className={cn("flex items-center", className)} />
 *
 * // Conditional classes
 * <button className={cn(
 *   "px-4 py-2 bg-blue-500",
 *   isActive && "bg-blue-700",
 *   isDisabled ? "opacity-50" : "hover:bg-blue-600"
 * )} />
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Validates a redirect path to prevent open redirect attacks.
 * Rejects protocol-relative URLs (//evil.com) and ensures paths are same-origin.
 * Preserves query strings and hash fragments from the original path.
 *
 * @param path - The path to validate (may include query strings and hash fragments)
 * @param fallback - The fallback path to return if validation fails (default: "/")
 * @returns A validated same-origin path with preserved query/hash, or the fallback
 */
export function validateRedirectPath(path: string, fallback = "/"): string {
  // Reject protocol-relative URLs (//evil.com)
  if (path.startsWith("//")) {
    return fallback;
  }

  // Only allow absolute paths starting with /
  if (!path.startsWith("/")) {
    return fallback;
  }

  // Validate the path by parsing it as a URL
  try {
    const url = new URL(path, "http://localhost");
    // Extract only the pathname to ensure it's same-origin
    // This prevents any host/port/protocol from being included
    const pathname = url.pathname;

    // Ensure the pathname is still a valid internal path
    if (!pathname.startsWith("/")) {
      return fallback;
    }

    return pathname + url.search + url.hash;
  } catch {
    return fallback;
  }
}
